import { parseDuration, UNITS_TO_SECONDS } from './durations';

const UNIT_ABBR: Record<keyof typeof UNITS_TO_SECONDS, string> = {
  minute: 'm',
  hour: 'h',
  day: 'd',
  week: 'w',
  month: 'mo',
  quarter: 'q',
  year: 'y',
};

const UNIT_SHORT_FRACTIONS = {
  quarter: 1,
  month: 1,
  week: 0.5,
  day: 1,
  hour: 1,
  minute: 1,
} as const;

const MONTHS_THRESHOLD_SECONDS = UNITS_TO_SECONDS.week * 12;
const MONTH_QUARTER_SECONDS = 3 * UNITS_TO_SECONDS.month;
const QUARTERS_DISPLAY_THRESHOLD_LOW = MONTH_QUARTER_SECONDS;
const QUARTERS_DISPLAY_THRESHOLD_HIGH = MONTH_QUARTER_SECONDS * 2;
const FUZZY_CEIL_THRESHOLD = 0.1;

export type ShortDurationRounding = 'floor' | 'ceil' | 'round';

export function formatShortDuration(seconds: number, rounding?: ShortDurationRounding): string;
export function formatShortDuration(longString: string, rounding?: ShortDurationRounding): string;
export function formatShortDuration(
  input: number | string,
  rounding: ShortDurationRounding = 'round',
): string {
  const totalSeconds = typeof input === 'string' ? parseDuration(input) : input;

  return formatInUnit(totalSeconds, selectShortUnit(totalSeconds), rounding);
}

export function formatFriendlyRangeParts(
  low: number | string,
  high: number | string,
): [string, string] {
  const lowSeconds = typeof low === 'string' ? parseDuration(low) : low;
  const highSeconds = typeof high === 'string' ? parseDuration(high) : high;
  const exactQuarters =
    lowSeconds % UNITS_TO_SECONDS.quarter === 0 && highSeconds % UNITS_TO_SECONDS.quarter === 0;
  const useQuarters =
    lowSeconds >= QUARTERS_DISPLAY_THRESHOLD_LOW &&
    (highSeconds >= QUARTERS_DISPLAY_THRESHOLD_HIGH || exactQuarters);
  const unit = useQuarters ? 'quarter' : selectShortUnit(lowSeconds);
  return [formatInUnit(lowSeconds, unit, 'floor'), formatInUnit(highSeconds, unit, 'ceil')];
}

export function formatFriendlyRange(low: number | string, high: number | string): string {
  const [lowStr, highStr] = formatFriendlyRangeParts(low, high);
  return lowStr === highStr ? lowStr : `${lowStr} – ${highStr}`;
}

function selectShortUnit(seconds: number): keyof typeof UNIT_SHORT_FRACTIONS {
  if (seconds >= MONTHS_THRESHOLD_SECONDS) return 'month';
  if (seconds >= UNITS_TO_SECONDS.week) return 'week';
  if (seconds >= UNITS_TO_SECONDS.day) return 'day';
  if (seconds >= UNITS_TO_SECONDS.hour) return 'hour';

  return 'minute';
}

function quartersValue(seconds: number): number {
  const rawQ = seconds / UNITS_TO_SECONDS.quarter;
  const monthQ = seconds / MONTH_QUARTER_SECONDS;
  const rawFrac = Math.abs(rawQ - Math.round(rawQ));
  const monthFrac = Math.abs(monthQ - Math.round(monthQ));

  return rawFrac <= monthFrac ? rawQ : monthQ;
}

function applyCeil(step: number): number {
  const fraction = step - Math.floor(step);

  // If the fractional part is very small, round down to avoid shortening "1w 1m" as "2w"
  return fraction < FUZZY_CEIL_THRESHOLD ? Math.floor(step) : Math.ceil(step);
}

function formatInUnit(
  seconds: number,
  unit: keyof typeof UNIT_SHORT_FRACTIONS,
  rounding: ShortDurationRounding,
): string {
  const fraction = UNIT_SHORT_FRACTIONS[unit];
  const value = unit === 'quarter' ? quartersValue(seconds) : seconds / UNITS_TO_SECONDS[unit];
  const step = value / fraction;
  const roundedStep = rounding === 'ceil' ? applyCeil(step) : Math[rounding](step);
  const rounded = roundedStep * fraction;

  if (rounded === 0) return '0';

  const precision = fraction < 1 ? String(fraction)?.split('.')[1]?.length : 0;
  const formatted = rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(precision);

  return `${formatted}${UNIT_ABBR[unit]}`;
}
